import type React from "react";
import {
	TextField,
	makeStyles,
	createStyles,
	type Theme,
} from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textArea: {
			width: "100%",
			"& .MuiInputBase-root": {
				padding: 0,
				overflow: "auto",
			},
			"& .MuiInputBase-input": {
				padding: "8px 12px",
				overflow: "auto !important",
				resize: "none",
			},
			"& .MuiOutlinedInput-notchedOutline": {
				border: "none",
			},
			"& .MuiOutlinedInput-root": {
				overflow: "auto",
				"&:hover .MuiOutlinedInput-notchedOutline": {
					border: "none",
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					border: "none",
				},
			},
			"& textarea": {
				overflow: "auto !important",
				resize: "none",
			},
		},
	}),
);

interface SimpleTextAreaControlProps {
	value: string;
	onChange?: (value: string) => void;
	disabled?: boolean;
	placeholder?: string;
	label?: string;
	rows?: number;
	maxRows?: number;
	minRows?: number;
	variant?: "standard" | "outlined" | "filled";
	fullWidth?: boolean;
	error?: boolean;
	helperText?: string;
	showColor?: boolean;
	styleText?: React.CSSProperties;
	className?: string;
	autoFocus?: boolean;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const SimpleTextAreaControl: React.FC<SimpleTextAreaControlProps> = ({
	value,
	onChange,
	disabled = false,
	placeholder,
	label,
	maxRows,
	minRows,
	variant = "outlined",
	fullWidth = true,
	error = false,
	helperText,
	showColor = true,
	styleText,
	className,
	autoFocus = false,
	onKeyDown,
	onBlur,
	onFocus,
}) => {
	const classes = useStyles();

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) onChange(event.target.value);
	};

	const combinedStyles = {
		...styleText,
		overflow: "auto",
		...(disabled && !showColor ? { color: "#666" } : {}),
	};

	return (
		<TextField
			value={value || ""}
			onChange={handleChange}
			disabled={disabled}
			placeholder={placeholder}
			label={label}
			multiline
			maxRows={maxRows}
			minRows={minRows}
			variant={variant}
			fullWidth={fullWidth}
			error={error}
			helperText={helperText}
			autoFocus={autoFocus}
			onKeyDown={onKeyDown}
			onBlur={onBlur}
			onFocus={onFocus}
			className={`${classes.textArea} ${className || ""}`}
			InputProps={{
				style: combinedStyles,
			}}
			inputProps={{
				style: {
					overflow: "auto",
					resize: "none",
				},
			}}
		/>
	);
};
